import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { Badge, Button, Card, Checkbox, ErrorText, Input } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe, useSetMe } from "@/lib/auth";
import type { User } from "@/lib/types";

interface Memory {
  id: string;
  content: string;
  source: "model" | "user" | "auto";
  updated_at: string;
}

export default function MemorySettings() {
  const me = useMe().data!;
  const setMe = useSetMe();
  const qc = useQueryClient();
  const memories = useQuery({ queryKey: ["memories"], queryFn: () => api.get<Memory[]>("/api/memories") });
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["memories"] });
  const setting = useMutation({
    mutationFn: (s: Record<string, boolean>) => api.patch<User>("/api/auth/me", { settings: s }),
    onSuccess: setMe,
  });
  const add = useMutation({
    mutationFn: () => api.post("/api/memories", { content: draft }),
    onSuccess: () => {
      setDraft("");
      refresh();
    },
  });
  const save = useMutation({
    mutationFn: (m: { id: string; text: string }) => api.patch(`/api/memories/${m.id}`, { content: m.text }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/memories/${id}`), onSuccess: refresh });
  const enabled = me.settings.memory_enabled !== false;

  return (
    <>
      <Card title="Memory">
        <div className="space-y-2">
          <Checkbox
            label="Let the assistant remember things about me across chats"
            checked={enabled}
            onChange={(e) => setting.mutate({ memory_enabled: e.target.checked })}
          />
          <Checkbox
            label="Also extract memories automatically when a chat has been quiet for a few minutes"
            checked={!!me.settings.auto_memory}
            disabled={!enabled}
            onChange={(e) => setting.mutate({ auto_memory: e.target.checked })}
          />
          <p className="text-xs text-zinc-500">Memories are shown to the model at the start of every chat. Temporary chats can read them but not add new ones.</p>
        </div>
      </Card>
      <Card title="What the assistant remembers">
        <form
          className="mb-4 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) add.mutate();
          }}
        >
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add something to remember" />
          <Button disabled={!draft.trim() || add.isPending}>
            <Plus size={16} /> Add
          </Button>
        </form>
        <ErrorText error={add.error || save.error || remove.error} />
        {memories.data?.length === 0 && <p className="text-sm text-zinc-500">Nothing yet.</p>}
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {memories.data?.map((m) => (
            <li key={m.id} className="flex items-center gap-2 py-2">
              {editing?.id === m.id ? (
                <form
                  className="flex flex-1 gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    save.mutate(editing);
                  }}
                >
                  <Input autoFocus value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
                  <Button size="icon" variant="ghost" aria-label="Save">
                    <Check size={16} />
                  </Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => setEditing(null)} aria-label="Cancel">
                    <X size={16} />
                  </Button>
                </form>
              ) : (
                <>
                  <p className="min-w-0 flex-1 text-sm">{m.content}</p>
                  {m.source !== "user" && <Badge>{m.source === "auto" ? "auto" : "assistant"}</Badge>}
                  <Button size="icon" variant="ghost" onClick={() => setEditing({ id: m.id, text: m.content })} aria-label="Edit">
                    <Pencil size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove.mutate(m.id)} aria-label="Delete">
                    <Trash2 size={14} />
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
